import { Fragment, useEffect, useMemo } from "react";
import { Link, generatePath, matchPath, useLocation } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface BreadcrumbItem {
  /** Text shown for this crumb. */
  label: string;
  /** Route pattern to link to. Omitted for group labels and for the current page. */
  path?: string;
}

interface BreadcrumbRoute {
  /** Route pattern, matched exactly against the current pathname. */
  pattern: string;
  /** Trail rendered after the home icon, outermost first. */
  crumbs: BreadcrumbItem[];
}

/**
 * Breadcrumb trail per admin route. Labels mirror AdminSidebar, and the
 * leading group label mirrors the sidebar section the route sits in.
 *
 * Static patterns must precede their parameterized siblings
 * (e.g. `/admin/tenants/new` before `/admin/tenants/:id`).
 */
const BREADCRUMB_ROUTES: BreadcrumbRoute[] = [
  { pattern: "/admin", crumbs: [{ label: "Dashboard" }] },

  // Customers
  {
    pattern: "/admin/users",
    crumbs: [{ label: "Customers" }, { label: "Users" }],
  },
  {
    pattern: "/admin/users/:id",
    crumbs: [
      { label: "Customers" },
      { label: "Users", path: "/admin/users" },
      { label: "User Detail" },
    ],
  },
  {
    pattern: "/admin/users/:id/edit",
    crumbs: [
      { label: "Customers" },
      { label: "Users", path: "/admin/users" },
      { label: "User Detail", path: "/admin/users/:id" },
      { label: "Edit" },
    ],
  },
  {
    pattern: "/admin/tenants",
    crumbs: [{ label: "Customers" }, { label: "Tenants" }],
  },
  {
    pattern: "/admin/tenants/new",
    crumbs: [
      { label: "Customers" },
      { label: "Tenants", path: "/admin/tenants" },
      { label: "New Tenant" },
    ],
  },
  // Before "/admin/tenants/:id", which would otherwise claim this literal as a tenant id.
  {
    pattern: "/admin/tenants/product-line-audit",
    crumbs: [
      { label: "Customers" },
      { label: "Tenants", path: "/admin/tenants" },
      { label: "Product Line Audit" },
    ],
  },
  // Before "/admin/tenants/:id", which would otherwise claim "move-vm" as a tenant id.
  {
    pattern: "/admin/tenants/move-vm",
    crumbs: [
      { label: "Customers" },
      { label: "Tenants", path: "/admin/tenants" },
      { label: "Move VM" },
    ],
  },
  {
    pattern: "/admin/tenants/:id",
    crumbs: [
      { label: "Customers" },
      { label: "Tenants", path: "/admin/tenants" },
      { label: "Tenant Detail" },
    ],
  },
  {
    pattern: "/admin/tenants/:id/transfer-ownership",
    crumbs: [
      { label: "Customers" },
      { label: "Tenants", path: "/admin/tenants" },
      { label: "Tenant Detail", path: "/admin/tenants/:id" },
      { label: "Move VM" },
    ],
  },
  {
    pattern: "/admin/tenants/:id/resources/:resourceId/transfer-ownership",
    crumbs: [
      { label: "Customers" },
      { label: "Tenants", path: "/admin/tenants" },
      { label: "Tenant Detail", path: "/admin/tenants/:id" },
      { label: "Move VM" },
    ],
  },
  {
    pattern: "/admin/tenants/:id/edit",
    crumbs: [
      { label: "Customers" },
      { label: "Tenants", path: "/admin/tenants" },
      { label: "Tenant Detail", path: "/admin/tenants/:id" },
      { label: "Edit" },
    ],
  },

  // Billing
  {
    pattern: "/admin/orders",
    crumbs: [{ label: "Billing" }, { label: "Orders" }],
  },
  {
    pattern: "/admin/orders/:orderId",
    crumbs: [
      { label: "Billing" },
      { label: "Orders", path: "/admin/orders" },
      { label: "Order Detail" },
    ],
  },
  {
    pattern: "/admin/payments",
    crumbs: [{ label: "Billing" }, { label: "Payments" }],
  },
  {
    pattern: "/admin/payments/verification",
    crumbs: [
      { label: "Billing" },
      { label: "Payments", path: "/admin/payments" },
      { label: "Payment Verification" },
    ],
  },
  {
    pattern: "/admin/payments/:paymentId",
    crumbs: [
      { label: "Billing" },
      { label: "Payments", path: "/admin/payments" },
      { label: "Payment Detail" },
    ],
  },
  {
    pattern: "/admin/billing-cycles",
    crumbs: [{ label: "Billing" }, { label: "Billing Cycles" }],
  },
  {
    // Named explicitly, or the fallback derives "Expiry Mismatches" from the path — the route the
    // screen was first built for, and now only half of what it reports.
    pattern: "/admin/billing-cycles/expiry-mismatches",
    crumbs: [
      { label: "Billing" },
      { label: "Billing Cycles", path: "/admin/billing-cycles" },
      { label: "Billing Consistency" },
    ],
  },
  {
    pattern: "/admin/e-invoices",
    crumbs: [{ label: "Billing" }, { label: "E-Invoices" }],
  },
  {
    pattern: "/admin/billing-info",
    crumbs: [{ label: "Billing" }, { label: "Billing Info" }],
  },
  {
    pattern: "/admin/balances",
    crumbs: [{ label: "Billing" }, { label: "Account Balances" }],
  },

  // Infrastructure
  {
    pattern: "/admin/resources",
    crumbs: [{ label: "Infrastructure" }, { label: "Resources" }],
  },
  {
    pattern: "/admin/resource-reinstalls/report",
    crumbs: [
      { label: "Infrastructure" },
      { label: "Resources", path: "/admin/resources" },
      { label: "VM Reinstall Report" },
    ],
  },
  {
    pattern: "/admin/resource-reinstalls",
    crumbs: [
      { label: "Infrastructure" },
      { label: "Resources", path: "/admin/resources" },
      { label: "VM Reinstall Log" },
    ],
  },
  {
    pattern: "/admin/resources/provision-for-user",
    crumbs: [
      { label: "Infrastructure" },
      { label: "Resources", path: "/admin/resources" },
      { label: "Provision For User" },
    ],
  },
  // The cross-resource Resource Actions page is unrouted (2026-09-11): the trail lives in the
  // "Resource Actions" tab of the resource detail page instead. Uncomment together with its route
  // in App.tsx to bring the page back.
  // {
  //   // Before "/admin/resources/:id", which would otherwise claim "actions" as a resource id.
  //   pattern: "/admin/resources/actions",
  //   crumbs: [
  //     { label: "Infrastructure" },
  //     { label: "Resources", path: "/admin/resources" },
  //     { label: "Resource Actions" },
  //   ],
  // },
  {
    pattern: "/admin/resources/:id",
    crumbs: [
      { label: "Infrastructure" },
      { label: "Resources", path: "/admin/resources" },
      { label: "Resource Detail" },
    ],
  },
  {
    pattern: "/admin/domains",
    crumbs: [{ label: "Infrastructure" }, { label: "Domains" }],
  },
  {
    pattern: "/admin/domains/operations",
    crumbs: [
      { label: "Infrastructure" },
      { label: "Domains", path: "/admin/domains" },
      { label: "Domain Operations" },
    ],
  },

  // Catalog
  {
    pattern: "/admin/products",
    crumbs: [{ label: "Catalog" }, { label: "Products" }],
  },
  {
    pattern: "/admin/products/new",
    crumbs: [
      { label: "Catalog" },
      { label: "Products", path: "/admin/products" },
      { label: "New Product" },
    ],
  },
  {
    pattern: "/admin/products/:id",
    crumbs: [
      { label: "Catalog" },
      { label: "Products", path: "/admin/products" },
      { label: "Product Detail" },
    ],
  },
  {
    pattern: "/admin/products/:id/pricing",
    crumbs: [
      { label: "Catalog" },
      { label: "Products", path: "/admin/products" },
      { label: "Product Detail", path: "/admin/products/:id" },
      { label: "Pricing" },
    ],
  },
  {
    pattern: "/admin/products/:id/pricing/:pricingId",
    crumbs: [
      { label: "Catalog" },
      { label: "Products", path: "/admin/products" },
      { label: "Product Detail", path: "/admin/products/:id" },
      { label: "Pricing", path: "/admin/products/:id/pricing" },
      { label: "Pricing Detail" },
    ],
  },
  {
    pattern: "/admin/product-lines",
    crumbs: [{ label: "Catalog" }, { label: "Product Lines" }],
  },
  {
    pattern: "/admin/product-lines/:id",
    crumbs: [
      { label: "Catalog" },
      { label: "Product Lines", path: "/admin/product-lines" },
      { label: "Product Line Detail" },
    ],
  },
  {
    pattern: "/admin/os-images",
    crumbs: [{ label: "Catalog" }, { label: "OS Images" }],
  },
  {
    pattern: "/admin/os-images/create",
    crumbs: [
      { label: "Catalog" },
      { label: "OS Images", path: "/admin/os-images" },
      { label: "New OS Image" },
    ],
  },
  {
    // No `/admin/os-images/:id` entry: the read-only detail page is gone, the edit form is the
    // image's page, so the trail goes straight from the list to Edit.
    pattern: "/admin/os-images/:id/edit",
    crumbs: [
      { label: "Catalog" },
      { label: "OS Images", path: "/admin/os-images" },
      { label: "Edit OS Image" },
    ],
  },
  {
    pattern: "/admin/pricing/components",
    crumbs: [{ label: "Catalog" }, { label: "Pricing Components" }],
  },
  {
    pattern: "/admin/pricing/components/:id",
    crumbs: [
      { label: "Catalog" },
      { label: "Pricing Components", path: "/admin/pricing/components" },
      { label: "Component Detail" },
    ],
  },
  {
    pattern: "/admin/pricing/prices",
    crumbs: [{ label: "Catalog" }, { label: "Component Prices" }],
  },
  {
    pattern: "/admin/billing-discounts",
    crumbs: [{ label: "Catalog" }, { label: "Billing Discounts" }],
  },
  {
    pattern: "/admin/tld-catalog",
    crumbs: [{ label: "Catalog" }, { label: "TLD Catalog" }],
  },

  // Reports
  {
    pattern: "/admin/reports/growth",
    crumbs: [{ label: "Reports" }, { label: "Growth Report" }],
  },
  {
    pattern: "/admin/domain-reports",
    crumbs: [{ label: "Reports" }, { label: "Domain Reports" }],
  },
  {
    pattern: "/admin/reports/wiservice",
    crumbs: [{ label: "Reports" }, { label: "WiService Status" }],
  },

  // Access & Security
  {
    pattern: "/admin/invites",
    crumbs: [{ label: "Access & Security" }, { label: "Admin Invitations" }],
  },
  {
    pattern: "/admin/invitation-codes",
    crumbs: [{ label: "Access & Security" }, { label: "Invitation Codes" }],
  },
  {
    pattern: "/admin/invitation-codes/new",
    crumbs: [
      { label: "Access & Security" },
      { label: "Invitation Codes", path: "/admin/invitation-codes" },
      { label: "New Invitation Code" },
    ],
  },
  {
    pattern: "/admin/invitation-codes/:id",
    crumbs: [
      { label: "Access & Security" },
      { label: "Invitation Codes", path: "/admin/invitation-codes" },
      { label: "Invitation Code Detail" },
    ],
  },
  {
    pattern: "/admin/api-keys",
    crumbs: [{ label: "Access & Security" }, { label: "API Keys" }],
  },
  {
    pattern: "/admin/audit",
    crumbs: [{ label: "Access & Security" }, { label: "Audit Logs" }],
  },
  {
    pattern: "/admin/notification-logs",
    crumbs: [{ label: "Access & Security" }, { label: "Notification Log" }],
  },

  // Configuration
  {
    pattern: "/admin/settings",
    crumbs: [{ label: "Configuration" }, { label: "Settings" }],
  },
  {
    pattern: "/admin/platform-settings",
    crumbs: [{ label: "Configuration" }, { label: "Platform Settings" }],
  },
  {
    pattern: "/admin/registrar-settings",
    crumbs: [{ label: "Configuration" }, { label: "Registrar Settings" }],
  },
  {
    pattern: "/admin/settings/exchange-rates",
    crumbs: [{ label: "Configuration" }, { label: "Exchange Rates" }],
  },
  {
    pattern: "/admin/settings/exchange-rates/:currency/history",
    crumbs: [
      { label: "Configuration" },
      { label: "Exchange Rates", path: "/admin/settings/exchange-rates" },
      { label: "History" },
    ],
  },
  {
    pattern: "/admin/tpaygate",
    crumbs: [{ label: "T-PayGate" }, { label: "Settings" }],
  },
  {
    pattern: "/admin/tpaygate/webhook-log",
    crumbs: [{ label: "T-PayGate" }, { label: "Webhook Log" }],
  },
  {
    pattern: "/admin/tpaygate/webhook-simulator",
    crumbs: [{ label: "T-PayGate" }, { label: "Simulator" }],
  },
  {
    pattern: "/admin/signup-bonus",
    crumbs: [{ label: "Configuration" }, { label: "Sign-up Bonus" }],
  },
  {
    pattern: "/admin/email-templates",
    crumbs: [{ label: "Configuration" }, { label: "Email Templates" }],
  },
  {
    pattern: "/admin/helper",
    crumbs: [{ label: "Configuration" }, { label: "Helper" }],
  },
  {
    pattern: "/admin/helper/jobs",
    crumbs: [
      { label: "Configuration" },
      { label: "Helper", path: "/admin/helper" },
      { label: "Job guide" },
    ],
  },
  {
    pattern: "/admin/helper/expired-vms",
    crumbs: [
      { label: "Configuration" },
      { label: "Helper", path: "/admin/helper" },
      { label: "Expired VMs" },
    ],
  },
];

/** Tab-title suffix; also the static `<title>` in index.html, shown before any route renders. */
const APP_NAME = "WiServices Admin";

const ID_SEGMENT_PATTERN = /^[0-9]+$|^[0-9a-f]{8}-[0-9a-f-]{27}$/i;

function toTitleCase(segment: string): string {
  return segment
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Builds the trail for a path with no declared route: title-cased segments,
 * with id-like segments dropped and nothing linked (the intermediate paths
 * are not guaranteed to be real routes).
 */
function buildFallbackCrumbs(pathname: string): BreadcrumbItem[] {
  return pathname
    .split("/")
    .filter(Boolean)
    .filter((segment) => segment !== "admin")
    .filter((segment) => !ID_SEGMENT_PATTERN.test(segment))
    .map((segment) => ({ label: toTitleCase(segment) }));
}

function buildBreadcrumbs(pathname: string): BreadcrumbItem[] {
  for (const route of BREADCRUMB_ROUTES) {
    const match = matchPath(route.pattern, pathname);
    if (!match) continue;

    return route.crumbs.map((crumb) => ({
      label: crumb.label,
      path: crumb.path ? generatePath(crumb.path, match.params) : undefined,
    }));
  }

  return buildFallbackCrumbs(pathname);
}

export function Breadcrumbs() {
  const location = useLocation();
  const items = useMemo(
    () => buildBreadcrumbs(location.pathname),
    [location.pathname],
  );

  // The tab title names the page the trail ends on — this table already labels every admin
  // route, so a second one for titles would only drift from it. Reset on unmount so the login
  // page does not keep the last screen's name.
  const pageLabel = items[items.length - 1]?.label;
  useEffect(() => {
    document.title = pageLabel ? `${pageLabel} | ${APP_NAME}` : APP_NAME;
    return () => {
      document.title = APP_NAME;
    };
  }, [pageLabel]);

  if (items.length === 0) return null;

  return (
    <nav
      aria-label="Breadcrumb"
      className="flex items-center gap-2 text-sm text-muted-foreground"
    >
      {items.map((item, index) => {
        const isLast = index === items.length - 1;

        return (
          <Fragment key={`${item.label}-${index}`}>
            {/* Separators sit between crumbs only — a single crumb stands alone. */}
            {index > 0 && <ChevronRight className="h-4 w-4" />}
            {isLast || !item.path ? (
              <span
                className={cn(
                  "truncate max-w-30 md:max-w-none",
                  isLast && "font-medium text-foreground max-w-45",
                )}
              >
                {item.label}
              </span>
            ) : (
              <Link
                to={item.path}
                className="hover:text-foreground transition-colors truncate max-w-30 md:max-w-none"
              >
                {item.label}
              </Link>
            )}
          </Fragment>
        );
      })}
    </nav>
  );
}
